import { Form, useFormState } from "react-final-form";
import React, {
  DispatchWithoutAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createListCollection } from "@ark-ui/react";
import classnames from "classnames";
import { Eye } from "lucide-react";

import { useTranslation } from "@/lib/i18n";
import type {
  ParameterTimeSeriesConfiguration,
  ParametersGeoBoundingBox,
  Widget,
} from "@/lib/api";
import { ModelMapBaseLayerStyle } from "@/lib/mapbox/base-layer-styles";
import { mapEditorSaveAction } from "@/features/widget-forms/dialogs/map-editor-save-action";
import { WidgetDialogProps } from "@/features/widget-forms/dialogs/widget-dialog-types";
import { canonicalizeWidgetUpdatePayload } from "@/features/widget-forms/dialogs/is-unchanged-widget-update";
import Dialog from "@/components/dialog/dialog";
import dialogVariants from "@/components/dialog/dialog-variants.module.css";
import { AddMapLayerModal } from "@/features/widget-forms/forms/weather-model-map/modals";
import AddStaticOverlayModal from "@/features/widget-forms/forms/weather-model-map/modals/add-static-overlay-modal";
import { TimeSeriesConfiguratorModal } from "@/features/widget-forms/forms/weather-model-map/modals/time-series-configurator-modal";
import { useMapPanelTabLayout } from "@/features/widget-forms/forms/map-widget-editor/use-map-panel-tab-layout";
import { BaseLayerStylePanel } from "@/features/widget-forms/forms/map-widget-editor/base-layer-style-panel";
import { InitialViewportPanel } from "@/features/widget-forms/forms/map-widget-editor/initial-viewport-panel";
import {
  MapWidgetEditableTitle,
  type MapWidgetEditableTitleStrings,
} from "@/features/widget-forms/forms/map-widget-editor/map-widget-editable-title";
import { MapWidgetEditorPreviewColumn } from "@/features/widget-forms/forms/map-widget-editor/map-widget-editor-preview-column";
import { MapWidgetDndProvider } from "@/features/widget-forms/forms/map-widget-editor/map-widget-dnd-provider";
import * as MapWidgetEditor from "@/features/widget-forms/forms/map-widget-editor/map-widget-editor";
import {
  MapWidgetTabRail,
  type MapWidgetTabRailItem,
} from "@/features/widget-forms/forms/map-widget-editor/map-widget-tab-rail";
import { PickModelWizard } from "@/features/widget-forms/forms/weather-model-map/pick-model-wizard";
import { initialViewportPresets } from "@/features/widget-forms/shared/initial-viewport-presets";
import { SortableItem, SortableList } from "@/features/admin/components/sortable-list";
import MapLayerCard from "@/features/widget-forms/forms/map-widget-editor/map-layer-card";
import StaticOverlayCard from "@/features/widget-forms/forms/weather-model-map/components/static-overlay-card";
import TimeSeriesConfigCard from "@/features/widget-forms/forms/weather-model-map/components/time-series-config-card";
import Icon from "@/components/icon/icon";
import { useWeatherModelMapForm } from "@/features/widget-forms/forms/weather-model-map/use-weather-model-map-form";
import Button from "@/components/button/button";
import Stack from "@/components/stack/stack";
import PreviewModal from "@/features/widget-forms/forms/weather-model-map/modals/preview-modal";
import {
  ModelMapPreview,
  ModelMapPreviewContent,
  ModelMapPreviewToolbar,
} from "@/features/widget-forms/forms/weather-model-map/model-map-preview";
import { WidgetContextHost } from "@/features/shared/widget-context/widget-context-host";
import {
  TimeSeriesChartPreviewProvider as ModelTimeSeriesChartPreviewProvider,
  TimeSeriesChartPreviewToolbar,
  TimeSeriesChartPreviewBody,
} from "@/features/widget-forms/forms/weather-model-map/weather-model-map-time-series-chart-preview";
import { useAdminDashboard } from "@/features/admin/hooks";
import { Badge } from "@/components/label/label";
import { useExitGuard } from "@/features/admin/hooks/use-exit-guard";
import DialogHeader from "@/components/dialog/dailog-header";

import styles from "./weather-model-map-dialog.module.css";
import {
  createEmptyTimeSeriesConfiguratorDraft,
  toTimeSeriesConfiguratorDraft,
} from "./time-series-configurator.utils";

enum WeatherModelMapTabs {
  MapLayers = "map-layers",
  TimeSeries = "time-series",
  InitialViewport = "initial-viewport",
  BaseLayerStyle = "base-layer-style",
}

function mergeInitialValues(
  initialValues: Partial<{
    title?: string;
    configuration?: {
      type?: string;
      initial_viewport_bounds?: ParametersGeoBoundingBox;
      base_layer_style?: string;
      parameter_time_series_configurations?: ParameterTimeSeriesConfiguration[];
      model_id?: string;
    };
  }>,
) {
  const config = initialValues?.configuration;
  if (!config || config.type !== "model_map") return initialValues;
  let configuration = { ...config };
  let changed = false;
  if (!configuration.initial_viewport_bounds) {
    configuration = {
      ...configuration,
      initial_viewport_bounds: initialViewportPresets.nl,
    };
    changed = true;
  }
  const style = configuration.base_layer_style;
  if (typeof style !== "string" || !style.trim()) {
    configuration = {
      ...configuration,
      base_layer_style: ModelMapBaseLayerStyle.Monochrome,
    };
    changed = true;
  }
  const ts = configuration.parameter_time_series_configurations;
  if (Array.isArray(ts)) {
    let tsChanged = false;
    const normalized = ts.map((c) => {
      const ok =
        typeof c.visualization_type === "string" && c.visualization_type.trim().length > 0;
      if (ok) return c;
      tsChanged = true;
      return { ...c, visualization_type: "line_graph" };
    });
    if (tsChanged) {
      configuration = {
        ...configuration,
        parameter_time_series_configurations: normalized,
      };
      changed = true;
    }
  }
  if (!changed) return initialValues;
  return {
    ...initialValues,
    configuration,
  };
}

export function WeatherModelMapDialog({
  initialValues,
  open,
  onOpenChange,
  onSubmit,
  onReset,
  isCreate = false,
}: WidgetDialogProps) {
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const hasChangesRef = useRef(false);

  const handleFormOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && hasChangesRef.current) {
      setConfirmationOpen(true);
    } else {
      onOpenChange(nextOpen);
    }
  };
  const handleConfirmClose = () => {
    onOpenChange(false);
    setConfirmationOpen(false);
    onReset();
  };
  const handleCancelClose = () => {
    setConfirmationOpen(false);
  };

  const mergedBase = useMemo(() => mergeInitialValues(initialValues), [initialValues]);
  const modelIdFromInitial = (
    mergedBase?.configuration as { model_id?: string } | undefined
  )?.model_id;

  const [wizardOutput, setWizardOutput] = useState<{
    title: string;
    model_id: string;
  } | null>(null);

  useEffect(() => {
    if (!open) {
      setWizardOutput(null);
    }
  }, [open]);

  const showEditor = Boolean(modelIdFromInitial) || wizardOutput !== null;

  const editorInitialValues = useMemo(() => {
    if (!wizardOutput) return mergedBase;
    return mergeInitialValues({
      ...mergedBase,
      title: wizardOutput.title,
      configuration: {
        ...(mergedBase.configuration as object),
        type: "model_map",
        model_id: wizardOutput.model_id,
      },
    });
  }, [mergedBase, wizardOutput]);

  const formMountKey = wizardOutput
    ? `wizard-${wizardOutput.model_id}`
    : `edit-${modelIdFromInitial ?? "none"}`;

  return (
    <>
      <Dialog open={open} onOpenChange={handleFormOpenChange} onClose={onReset}>
        <Dialog.Content className={classnames(dialogVariants.fullscreen, styles.dialog)}>
          {showEditor ? (
            <Form
              key={formMountKey}
              initialValues={editorInitialValues}
              onSubmit={(values) =>
                onSubmit(canonicalizeWidgetUpdatePayload(values as Widget))
              }
            >
              {() => (
                <EditForm
                  isCreate={isCreate}
                  onClose={() => {
                    if (hasChangesRef.current) {
                      setConfirmationOpen(true);
                    } else {
                      onOpenChange(false);
                      onReset();
                    }
                  }}
                  hasChangesRef={hasChangesRef}
                />
              )}
            </Form>
          ) : (
            <PickModelWizard
              onComplete={setWizardOutput}
              onCancel={() => setConfirmationOpen(true)}
            />
          )}
        </Dialog.Content>
      </Dialog>
      <ConfirmationDialog
        open={confirmationOpen}
        onConfirm={handleConfirmClose}
        onCancel={handleCancelClose}
      />
    </>
  );
}

function ConfirmationDialog({
  open,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  onConfirm: React.DispatchWithoutAction;
  onCancel: React.DispatchWithoutAction;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open}>
      <Dialog.Content
        className={classnames(dialogVariants.small, styles.dialog, styles.confirmation)}
      >
        <h1>{t("admin.widgets.fields.map_editor.confirmation.title")}</h1>
        <span>{t("admin.widgets.fields.map_editor.confirmation.text")}</span>
        <div className={styles.confirmationButtons}>
          <Button type="button" variant="solid" onClick={onCancel}>
            {t("admin.widgets.fields.map_editor.confirmation.cancel")}
          </Button>
          <Button type="button" variant="primary" onClick={onConfirm}>
            {t("admin.widgets.fields.map_editor.confirmation.confirm")}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}

function EditForm({
  isCreate,
  onClose,
  hasChangesRef,
}: {
  isCreate: boolean;
  onClose: DispatchWithoutAction;
  hasChangesRef: React.MutableRefObject<boolean>;
}) {
  const { t } = useTranslation();
  const { submitting } = useFormState({ subscription: { submitting: true } });
  const {
    hasChanges,
    dirtyBaselineReady,
    form,
    title,
    configuration,
    initialViewportBounds,
    setInitialViewportBounds,
    updateInitialViewportBoundsField,
    baseLayerStyle,
    setBaseLayerStyle,
  } = useWeatherModelMapForm();
  hasChangesRef.current = hasChanges;

  const tabItems: MapWidgetTabRailItem[] = useMemo(
    () => [
      {
        label: t("admin.widgets.fields.model_map.tabs.map_layers"),
        value: WeatherModelMapTabs.MapLayers,
        icon: "layers",
      },
      {
        label: t("admin.widgets.fields.model_map.tabs.time_series"),
        value: WeatherModelMapTabs.TimeSeries,
        icon: "chart-line",
      },
      {
        label: t("admin.widgets.fields.model_map.tabs.initial_viewport"),
        value: WeatherModelMapTabs.InitialViewport,
        icon: "map",
      },
      {
        label: t("admin.widgets.fields.model_map.tabs.base_layer_style"),
        value: WeatherModelMapTabs.BaseLayerStyle,
        icon: "palette",
      },
    ],
    [],
  );

  const [activeTab, setActiveTab] = useState<WeatherModelMapTabs>(
    WeatherModelMapTabs.MapLayers,
  );

  const [previewDialogVisible, setPreviewDialogVisible] = useState(false);
  const dashboard = useAdminDashboard(false);

  const initialViewportStrings = useMemo(
    () => ({
      title: t("admin.widgets.fields.map_editor.initial_viewport.title"),
      subtitle: t("admin.widgets.fields.map_editor.initial_viewport.subtitle"),
      manualTitle: t("admin.widgets.fields.map_editor.initial_viewport.manual_title"),
      northLatitude: t("admin.widgets.fields.map_editor.initial_viewport.north_latitude"),
      westLongitude: t("admin.widgets.fields.map_editor.initial_viewport.west_longitude"),
      eastLongitude: t("admin.widgets.fields.map_editor.initial_viewport.east_longitude"),
      southLatitude: t("admin.widgets.fields.map_editor.initial_viewport.south_latitude"),
      presetsTitle: t("admin.widgets.fields.map_editor.initial_viewport.presets_title"),
      presetNl: t("admin.widgets.fields.map_editor.initial_viewport.preset_nl"),
      presetNlDescription: t(
        "admin.widgets.fields.map_editor.initial_viewport.preset_nl_description",
      ),
      presetBes: t("admin.widgets.fields.map_editor.initial_viewport.preset_bes"),
      presetBesDescription: t(
        "admin.widgets.fields.map_editor.initial_viewport.preset_bes_description",
      ),
      selectedStatusText: t("admin.widgets.fields.model_map.map_layers.selected"),
    }),
    [],
  );

  const baseLayerStrings = useMemo(
    () => ({
      title: t("admin.widgets.fields.map_editor.base_layer_style.title"),
      subtitle: t("admin.widgets.fields.map_editor.base_layer_style.subtitle"),
      styleLabel: t("admin.widgets.fields.map_editor.base_layer_style.style_label"),
    }),
    [],
  );

  const baseLayerStyleCollection = useMemo(
    () =>
      createListCollection<{ label: string; value: string }>({
        items: [
          {
            label: t("admin.widgets.fields.model_map.base_layer_style.option_monochrome"),
            value: ModelMapBaseLayerStyle.Monochrome,
          },
          {
            label: t("admin.widgets.fields.model_map.base_layer_style.option_full_color"),
            value: ModelMapBaseLayerStyle.FullColor,
          },
        ],
      }),
    [],
  );

  const previewColumnStrings = useMemo(
    () => ({
      previewTitle: t("admin.widgets.fields.map_editor.preview.title"),
      previewButton: t("admin.widgets.fields.map_editor.preview.button"),
    }),
    [],
  );

  const titleStrings: MapWidgetEditableTitleStrings = useMemo(
    () => ({
      untitled: t("admin.widgets.fields.model_map.general.untitled"),
      nameLabel: t("admin.widgets.fields.name.label"),
      confirmTitleAria: t("admin.widgets.fields.model_map.general.confirm_title"),
      cancelEditingAria: t("admin.widgets.fields.model_map.general.cancel_editing"),
      editTitleAria: t("admin.widgets.fields.model_map.general.edit_title"),
    }),
    [],
  );

  const handleTitleChange = useCallback(
    (next: string) => {
      form.change("title", next);
    },
    [form],
  );

  const {
    horizontal: horizontalTabs,
    orientation: tabRailOrientation,
    variant: horizontalTabVariant,
  } = useMapPanelTabLayout();

  const preventNativeSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const boundsInteractionMode =
    activeTab === WeatherModelMapTabs.InitialViewport
      ? ("editable" as const)
      : ("none" as const);

  const settingsPanel =
    activeTab === WeatherModelMapTabs.MapLayers ? (
      <MapLayersTab />
    ) : activeTab === WeatherModelMapTabs.TimeSeries ? (
      <TimeSeriesTab />
    ) : activeTab === WeatherModelMapTabs.InitialViewport ? (
      <InitialViewportPanel
        strings={initialViewportStrings}
        bounds={initialViewportBounds}
        baseLayerStyle={baseLayerStyle}
        onBoundsChange={setInitialViewportBounds}
        onCoordChange={updateInitialViewportBoundsField}
      />
    ) : (
      <BaseLayerStylePanel
        strings={baseLayerStrings}
        collection={baseLayerStyleCollection}
        value={baseLayerStyle}
        onValueChange={setBaseLayerStyle}
        fieldName="configuration.base_layer_style"
        previewBounds={initialViewportBounds}
        baseLayerStyle={baseLayerStyle}
      />
    );

  useExitGuard();

  return (
    <MapWidgetDndProvider>
      <MapWidgetEditor.Root
        onSubmit={preventNativeSubmit}
        className={horizontalTabs ? styles.panelGridHorizontalTabs : undefined}
      >
        <MapWidgetEditor.Header>
          <MapWidgetEditableTitle
            value={title}
            onChange={handleTitleChange}
            strings={titleStrings}
            variant="auto"
          />
          <div className={styles.subtitle}>
            Model: <ModelNameBadge />
          </div>
          <Button
            type="button"
            variant="card"
            className={styles.previewButton}
            onClick={() => setPreviewDialogVisible(true)}
          >
            <Stack gap={8} alignItems="center">
              <Eye size={16} />
              <span>{previewColumnStrings.previewButton}</span>
            </Stack>
          </Button>
          <WidgetContextHost dashboardId={dashboard?.id}>
            <PreviewModal
              configuration={configuration}
              previewNode={<ModelMapPreviewContent configuration={configuration} />}
              toolbar={<ModelMapPreviewToolbar />}
              open={previewDialogVisible}
              onOpenChange={setPreviewDialogVisible}
              title={title}
            />
          </WidgetContextHost>
        </MapWidgetEditor.Header>
        <MapWidgetEditor.Nav>
          <MapWidgetTabRail
            items={tabItems}
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as WeatherModelMapTabs)}
            orientation={tabRailOrientation}
            variant={horizontalTabVariant}
          />
        </MapWidgetEditor.Nav>
        <MapWidgetEditor.Settings>{settingsPanel}</MapWidgetEditor.Settings>
        <MapWidgetEditor.Preview>
          <MapWidgetEditorPreviewColumn
            bounds={initialViewportBounds}
            baseLayerStyle={baseLayerStyle}
            onBoundsChange={setInitialViewportBounds}
            boundsInteractionMode={boundsInteractionMode}
            fitBoundsPadding={activeTab === WeatherModelMapTabs.InitialViewport ? 40 : 0}
            onExpand={() => setPreviewDialogVisible(true)}
            strings={previewColumnStrings}
            content={
              activeTab === WeatherModelMapTabs.InitialViewport ? undefined : (
                <ModelMapPreview configuration={configuration} />
              )
            }
          />
        </MapWidgetEditor.Preview>
        <MapWidgetEditor.Footer>
          <Stack justifyContent="flex-end" gap={8}>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t("general.cancel")}
            </Button>
            <Button
              type="button"
              variant="solid"
              onClick={() => {
                if (
                  mapEditorSaveAction({ isCreate, dirtyBaselineReady, hasChanges }) ===
                  "close"
                ) {
                  onClose();
                  return;
                }
                form.submit();
              }}
              loading={submitting || !title.trim()}
            >
              {t("general.save")}
              <Icon name="save" />
            </Button>
          </Stack>
        </MapWidgetEditor.Footer>
      </MapWidgetEditor.Root>
    </MapWidgetDndProvider>
  );
}

function ModelNameBadge() {
  const { t } = useTranslation();
  const { selectedModel, selectedModelId } = useWeatherModelMapForm();
  const text =
    selectedModel?.name ??
    selectedModelId ??
    t("admin.widgets.fields.model_map.general.no_model_selected");
  return <Badge>{text}</Badge>;
}

function MapLayersTab() {
  return (
    <>
      <ModelMapParameterLayersPanel />
      <ModelMapStaticOverlaysPanel />
    </>
  );
}

function ModelMapParameterLayersPanel() {
  const { t } = useTranslation();
  const {
    mapLayers,
    selectedModel,
    selectedMapLayers,
    addMapLayer,
    removeMapLayer,
    reorderMapLayers,
    renameMapLayer,
  } = useWeatherModelMapForm();

  return (
    <div className={styles.settingsPanel}>
      <div className={styles.sectionHeader}>
        <AddMapLayerModal
          mapLayers={mapLayers ?? []}
          selectedMapLayerIds={selectedMapLayers.map((it) => it.parameter_map_layer_id)}
          onAddMapLayer={addMapLayer}
          selectedModelName={selectedModel?.name}
        />
      </div>
      <div className={styles.settingsPanelCards}>
        {selectedMapLayers.length > 0 ? (
          <SortableList
            onReorder={reorderMapLayers}
            className={styles.cardGrid}
            groupId="maplayers"
          >
            {selectedMapLayers.map((mapLayer, index) => (
              <SortableItem key={mapLayer.parameter_map_layer_id} index={index}>
                <MapLayerCard
                  mapLayer={mapLayer}
                  layerInfo={mapLayers?.find(
                    (m) => m.id === mapLayer.parameter_map_layer_id,
                  )}
                  onRename={(text) => renameMapLayer(mapLayer.parameter_map_layer_id, text)}
                  onRemove={() => removeMapLayer(mapLayer.parameter_map_layer_id)}
                />
              </SortableItem>
            ))}
          </SortableList>
        ) : (
          <div className={styles.emptyState}>
            <Icon name="map" size={32} />
            <span className={styles.emptyStateText}>
              {t("admin.widgets.fields.model_map.map_layers.empty")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function ModelMapStaticOverlaysPanel() {
  const { t } = useTranslation();
  const {
    staticOverlays,
    staticOverlayConfigurations,
    selectedStaticOverlayIds,
    addStaticOverlay,
    removeStaticOverlay,
    reorderStaticOverlays,
    renameStaticOverlay,
    updateStaticOverlayColor,
    updateStaticOverlayEnabledByDefault,
  } = useWeatherModelMapForm();

  return (
    <div className={styles.settingsPanel}>
      <div className={styles.sectionHeader}>
        <AddStaticOverlayModal
          staticOverlays={staticOverlays ?? []}
          selectedStaticOverlayIds={selectedStaticOverlayIds}
          onAdd={addStaticOverlay}
        />
      </div>
      <div className={styles.settingsPanelCards}>
        {staticOverlayConfigurations.length > 0 ? (
          <SortableList
            onReorder={reorderStaticOverlays}
            className={styles.cardGrid}
            groupId="static-overlays"
          >
            {staticOverlayConfigurations.map((overlay, index) => (
              <SortableItem key={overlay.static_map_overlay_id} index={index}>
                <StaticOverlayCard
                  config={overlay}
                  overlayInfo={staticOverlays?.find(
                    (o) => o.id === overlay.static_map_overlay_id,
                  )}
                  onRename={(text) =>
                    renameStaticOverlay(overlay.static_map_overlay_id, text)
                  }
                  onColorChange={(color) =>
                    updateStaticOverlayColor(overlay.static_map_overlay_id, color)
                  }
                  onEnabledByDefaultChange={(enabled) =>
                    updateStaticOverlayEnabledByDefault(
                      overlay.static_map_overlay_id,
                      enabled,
                    )
                  }
                  onRemove={() => removeStaticOverlay(overlay.static_map_overlay_id)}
                />
              </SortableItem>
            ))}
          </SortableList>
        ) : (
          <div className={styles.emptyState}>
            <Icon name="shapes" size={32} />
            <span className={styles.emptyStateText}>
              {t("admin.widgets.fields.model_map.static_overlays.empty")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function TimeSeriesTab() {
  const { t } = useTranslation();
  const {
    parameters,
    parametersLoading,
    unitsError,
    timeSeriesConfigurations,
    addTimeSeriesConfig,
    removeTimeSeriesConfig,
    reorderTimeSeriesConfigs,
    updateTimeSeriesConfig,
    selectedModelId,
  } = useWeatherModelMapForm();
  const dashboard = useAdminDashboard(false);
  const [configuratorOpen, setConfiguratorOpen] = useState(false);
  const [configuratorIndex, setConfiguratorIndex] = useState<number | null>(null);
  const [removeConfirmIndex, setRemoveConfirmIndex] = useState<number | null>(null);

  const handleAddTimeSeries = () => {
    setConfiguratorIndex(null);
    setConfiguratorOpen(true);
  };

  const handleEditConfig = (index: number) => {
    setConfiguratorIndex(index);
    setConfiguratorOpen(true);
  };

  const handleConfiguratorDone = (config: ParameterTimeSeriesConfiguration) => {
    if (configuratorIndex === null) {
      addTimeSeriesConfig(config);
    } else {
      updateTimeSeriesConfig(configuratorIndex, config);
    }
    setConfiguratorOpen(false);
    setConfiguratorIndex(null);
  };

  const handleConfiguratorCancel = () => {
    setConfiguratorOpen(false);
    setConfiguratorIndex(null);
  };

  const initialConfig =
    configuratorIndex !== null && timeSeriesConfigurations[configuratorIndex]
      ? toTimeSeriesConfiguratorDraft(timeSeriesConfigurations[configuratorIndex])
      : createEmptyTimeSeriesConfiguratorDraft();

  return (
    <div className={styles.settingsPanel}>
      <div className={styles.sectionHeader}>
        <Button type="button" variant="solid" onClick={handleAddTimeSeries}>
          <Stack gap={8} alignItems="center">
            <Icon name="plus" size={16} />
            <span>{t("admin.widgets.fields.model_map.time_series.add")}</span>
          </Stack>
        </Button>
      </div>
      <div className={styles.settingsPanelCards}>
        {timeSeriesConfigurations.length > 0 ? (
          <SortableList
            onReorder={reorderTimeSeriesConfigs}
            className={styles.cardGrid}
            groupId="timeseries"
          >
            {timeSeriesConfigurations.map((config, index) => (
              <SortableItem key={`${config.title}-${index}`} index={index}>
                <TimeSeriesConfigCard
                  config={config}
                  parameters={parameters ?? []}
                  onEdit={() => handleEditConfig(index)}
                  onRemove={() => setRemoveConfirmIndex(index)}
                />
              </SortableItem>
            ))}
          </SortableList>
        ) : (
          <div className={styles.emptyState}>
            <Icon name="chart-line" size={32} />
            <span className={styles.emptyStateText}>
              {t("admin.widgets.fields.model_map.parameters.empty")}
            </span>
          </div>
        )}
      </div>
      <TimeSeriesConfiguratorModal
        open={configuratorOpen}
        onOpenChange={setConfiguratorOpen}
        initialConfig={initialConfig}
        parameters={parameters ?? []}
        parametersLoading={parametersLoading}
        unitsError={unitsError}
        timeSeriesConfigurations={timeSeriesConfigurations}
        configuratorIndex={configuratorIndex}
        onDone={handleConfiguratorDone}
        onCancel={handleConfiguratorCancel}
        renderPreview={(liveConfig) => ({
          wrap: (node) => (
            <WidgetContextHost dashboardId={dashboard?.id}>
              <ModelTimeSeriesChartPreviewProvider
                config={liveConfig}
                parameters={parameters ?? []}
                modelId={selectedModelId}
              >
                {node}
              </ModelTimeSeriesChartPreviewProvider>
            </WidgetContextHost>
          ),
          toolbar: <TimeSeriesChartPreviewToolbar />,
          content: <TimeSeriesChartPreviewBody />,
        })}
      />
      <RemoveConfigConfirmDialog
        open={removeConfirmIndex !== null}
        onConfirm={() => {
          if (removeConfirmIndex !== null) {
            removeTimeSeriesConfig(removeConfirmIndex);
            setRemoveConfirmIndex(null);
          }
        }}
        onCancel={() => setRemoveConfirmIndex(null)}
      />
    </div>
  );
}

function RemoveConfigConfirmDialog({
  open,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  configTitle?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <Dialog.Content className={dialogVariants.default}>
        <DialogHeader.Root>
          <DialogHeader.Title>
            {t("admin.widgets.fields.model_map.time_series.remove_config_confirm")}
          </DialogHeader.Title>
        </DialogHeader.Root>
        <Stack justifyContent="flex-end" gap={8} flexDirection="row">
          <Button type="button" variant="ghost" onClick={onCancel}>
            {t("general.cancel")}
          </Button>
          <Button type="button" variant="solid" onClick={onConfirm}>
            {t("admin.widgets.fields.model_map.time_series.remove_config_confirm_button")}
          </Button>
        </Stack>
      </Dialog.Content>
    </Dialog>
  );
}
